import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Eye, EyeOff, ArrowRight, AlertCircle } from 'lucide-react';
import Logo from '../components/Logo';
import { useReCaptchaToken } from '../hooks/useReCaptcha';
import { validateRecaptchaToken, isRecaptchaConfigured } from '../services/recaptchaService';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [cnpj, setCnpj] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, userProfile, isAdmin } = useAuth();
  const navigate = useNavigate();
  const { getReCaptchaToken } = useReCaptchaToken();

  // Redirecionar se já estiver logado
  useEffect(() => {
    if (userProfile && isAdmin) {
      console.log('🔄 Usuário já logado, redirecionando...');
      navigate('/');
    }
  }, [userProfile, isAdmin, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !password || !cnpj) {
      setError('Por favor, preencha todos os campos');
      return;
    }

    // Validar formato do CNPJ (apenas números)
    const cnpjNumbers = cnpj.replace(/\D/g, '');
    if (cnpjNumbers.length !== 14) {
      setError('CNPJ deve ter 14 dígitos');
      return;
    }

    // Verificar reCAPTCHA e validar se está configurado
    const recaptchaEnabled = isRecaptchaConfigured();
    const isDevelopment = process.env.NODE_ENV === 'development';
    
    // Em desenvolvimento, permitir pular reCAPTCHA se necessário
    const skipRecaptchaInDev = process.env.REACT_APP_SKIP_RECAPTCHA_DEV === 'true';
    
    if (recaptchaEnabled && !(isDevelopment && skipRecaptchaInDev)) {
      try {
        const recaptchaToken = await getReCaptchaToken();
        
        if (!recaptchaToken) {
          if (isDevelopment) {
            console.warn('⚠️ Token reCAPTCHA não gerado. Continuando em desenvolvimento...');
            // Em desenvolvimento, permite continuar sem token
          } else {
            setError('Falha na verificação de segurança. Por favor, recarregue a página e tente novamente.');
            return;
          }
        } else {
          // Validar o token do reCAPTCHA
          const validation = await validateRecaptchaToken(recaptchaToken);
          
          if (!validation.success) {
            // Em desenvolvimento, ser mais permissivo
            if (isDevelopment) {
              console.warn(`⚠️ Score baixo (${validation.score?.toFixed(2) || 'N/A'}), mas permitindo em desenvolvimento.`);
              // Em desenvolvimento, permite continuar mesmo com score baixo
            } else {
              setError('Atividade suspeita detectada. Acesso negado por segurança.');
              console.warn('🚫 Bot detectado! Score:', validation.score, 'Erro:', validation.error);
              return;
            }
          } else {
            // Log do score para monitoramento (apenas em desenvolvimento)
            if (isDevelopment) {
              console.log('✅ reCAPTCHA validado. Score:', validation.score);
            }
          }
        }
      } catch (error: any) {
        if (isDevelopment) {
          console.warn('⚠️ Erro na validação do reCAPTCHA em desenvolvimento:', error);
          // Em desenvolvimento, permite continuar mesmo com erro
        } else {
          console.error('Erro na validação do reCAPTCHA:', error);
          setError('Erro na verificação de segurança. Tente novamente.');
          return;
        }
      }
    } else {
      // Em desenvolvimento, apenas avisa que o reCAPTCHA não está configurado
      if (isDevelopment) {
        console.warn('⚠️ reCAPTCHA não está configurado ou desabilitado em desenvolvimento. Login continuará sem proteção contra bots.');
      }
    }

    try {
      setError('');
      setLoading(true);
      
      await login(email, password, cnpjNumbers);
      console.log('🔄 Login bem-sucedido, redirecionando...');
      navigate('/');
    } catch (error: any) {
      console.error('Erro no login:', error);
      
      // Traduzir erros do Firebase para português
      let errorMessage = 'Falha ao fazer login';
      
      if (error.code) {
        switch (error.code) {
          case 'auth/invalid-credential':
          case 'auth/wrong-password':
          case 'auth/user-not-found':
            errorMessage = 'Email ou senha incorretos. Verifique suas credenciais.';
            break;
          case 'auth/invalid-email':
            errorMessage = 'Email inválido. Verifique o formato do email.';
            break;
          case 'auth/user-disabled':
            errorMessage = 'Esta conta foi desativada. Entre em contato com o suporte.';
            break;
          case 'auth/too-many-requests':
            errorMessage = 'Muitas tentativas de login. Tente novamente mais tarde.';
            break;
          case 'auth/network-request-failed':
            errorMessage = 'Erro de conexão. Verifique sua internet e tente novamente.';
            break;
          default:
            // Se a mensagem de erro já estiver em português, usar ela
            if (error.message && !error.message.includes('auth/')) {
              errorMessage = error.message;
            } else {
              errorMessage = error.message || 'Falha ao fazer login. Verifique suas credenciais.';
            }
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col relative overflow-hidden">
      {/* Padrões de ondas decorativas */}
      <div className="absolute top-0 right-0 w-96 h-96 opacity-10">
        <svg viewBox="0 0 400 400" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M0,200 Q100,150 200,200 T400,200"
            stroke="#3B82F6"
            strokeWidth="3"
            fill="none"
            className="animate-pulse"
          />
          <path
            d="M0,250 Q100,200 200,250 T400,250"
            stroke="#3B82F6"
            strokeWidth="3"
            fill="none"
            className="animate-pulse"
            style={{ animationDelay: '0.5s' }}
          />
        </svg>
      </div>
      <div className="absolute bottom-0 right-0 w-96 h-96 opacity-10">
        <svg viewBox="0 0 400 400" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M0,150 Q100,100 200,150 T400,150"
            stroke="#3B82F6"
            strokeWidth="3"
            fill="none"
            className="animate-pulse"
            style={{ animationDelay: '1s' }}
          />
          <path
            d="M0,200 Q100,150 200,200 T400,200"
            stroke="#3B82F6"
            strokeWidth="3"
            fill="none"
            className="animate-pulse"
            style={{ animationDelay: '1.5s' }}
          />
        </svg>
      </div>

      {/* Card de Login */}
      <div className="flex-1 flex items-center justify-center px-4 py-12 relative z-10">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 sm:p-10 relative z-10">
            {/* Logo maior acima do formulário */}
            <div className="flex justify-center mb-6">
              <Logo size="large" showText={false} />
            </div>

            {/* Título */}
            <h1 className="text-3xl font-bold text-gray-900 text-center mb-2">
              Painel administrativo
            </h1>
            <p className="text-sm text-gray-600 text-center mb-8">
              Acesso restrito para administradores
            </p>

            <form className="space-y-5" onSubmit={handleSubmit}>
              {/* Error Message */}
              {error && (
                <div className="rounded-lg bg-red-50 border border-red-200 p-4">
                  <div className="flex items-start">
                    <AlertCircle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                    <div className="ml-3">
                      <p className="text-sm font-medium text-red-800">
                        {error}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* CNPJ */}
              <div>
                <label htmlFor="cnpj" className="block text-sm font-medium text-gray-700 mb-2">
                  CNPJ da Empresa
                </label>
                <input
                  id="cnpj"
                  name="cnpj"
                  type="text"
                  required
                  value={cnpj}
                  onChange={(e) => {
                    // Formatar CNPJ automaticamente
                    let value = e.target.value.replace(/\D/g, '');
                    if (value.length <= 14) {
                      value = value.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
                      setCnpj(value);
                    }
                  }}
                  className="w-full px-4 py-3 border border-blue-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  placeholder="00.000.000/0000-00"
                  maxLength={18}
                />
              </div>

              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 border border-blue-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  placeholder="seu@email.com"
                />
              </div>

              {/* Password */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                  Senha
                </label>
                <div className="relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-4 py-3 pr-12 border border-blue-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    placeholder="Sua senha"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>

              {/* reCAPTCHA Badge */}
              <div className="flex items-center justify-center gap-2 text-xs text-gray-500">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" fill="currentColor"/>
                </svg>
                <span>Protegido por reCAPTCHA</span>
              </div>

              {/* Submit Button */}
              <div className="pt-2">
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-800 text-white font-medium rounded-lg hover:from-blue-600 hover:to-blue-900 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                      <span>Entrando...</span>
                    </>
                  ) : (
                    <>
                      <span>Entrar</span>
                      <ArrowRight className="h-5 w-5" />
                    </>
                  )}
                </button>
              </div>
            </form>

            {/* Informações */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <div className="text-center">
                <p className="text-xs text-gray-500">
                  Problemas para entrar?{' '}
                  <a href="#" className="text-blue-600 hover:text-blue-700 font-medium">
                    Entre em contato com o suporte
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Ondas animadas na parte inferior */}
      <div className="absolute bottom-0 left-0 right-0 h-64 overflow-hidden pointer-events-none z-0">
        <svg
          className="absolute bottom-0 w-full h-full"
          viewBox="0 0 1200 300"
          preserveAspectRatio="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <linearGradient id="waveGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#3B82F6" stopOpacity="0.3" />
              <stop offset="50%" stopColor="#2563EB" stopOpacity="0.5" />
              <stop offset="100%" stopColor="#1E40AF" stopOpacity="0.7" />
            </linearGradient>
            <linearGradient id="waveGradient2" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#60A5FA" stopOpacity="0.2" />
              <stop offset="50%" stopColor="#3B82F6" stopOpacity="0.4" />
              <stop offset="100%" stopColor="#2563EB" stopOpacity="0.6" />
            </linearGradient>
            <linearGradient id="waveGradient3" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#93C5FD" stopOpacity="0.15" />
              <stop offset="50%" stopColor="#60A5FA" stopOpacity="0.3" />
              <stop offset="100%" stopColor="#3B82F6" stopOpacity="0.5" />
            </linearGradient>
          </defs>
          
          {/* Onda 1 - Mais profunda */}
          <g className="wave-container-1">
            <path
              d="M-200,150 Q100,100 400,150 T1000,150 T1600,150 L1600,300 L-200,300 Z"
              fill="url(#waveGradient)"
            />
          </g>
          
          {/* Onda 2 - Média */}
          <g className="wave-container-2">
            <path
              d="M-200,180 Q100,130 400,180 T1000,180 T1600,180 L1600,300 L-200,300 Z"
              fill="url(#waveGradient2)"
            />
          </g>
          
          {/* Onda 3 - Superficial */}
          <g className="wave-container-3">
            <path
              d="M-200,200 Q100,150 400,200 T1000,200 T1600,200 L1600,300 L-200,300 Z"
              fill="url(#waveGradient3)"
            />
          </g>
        </svg>
      </div>

      <style>{`
        @keyframes waveMove1 {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(200px);
          }
        }
        @keyframes waveMove2 {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(200px);
          }
        }
        @keyframes waveMove3 {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(200px);
          }
        }
        .wave-container-1 {
          animation: waveMove1 8s linear infinite;
        }
        .wave-container-2 {
          animation: waveMove2 6s linear infinite;
          animation-delay: 1s;
        }
        .wave-container-3 {
          animation: waveMove3 10s linear infinite;
          animation-delay: 2s;
        }
      `}</style>
    </div>
  );
};

export default Login;
